import React, { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Pencil, Plus, RefreshCw } from "lucide-react";
import Page from "components/Page";
import SectionCard from "components/SectionCard";
import { useDialog } from "context/DialogContext";
import { useProfile } from "context/ProfileContext";
import ClassificationModal from "./components/ClassificationModal";
import ClassificationValueModal from "./components/ClassificationValueModal";
import { flattenClassificationHierarchy, getClassificationAncestors, getClassificationPath } from "./lib/hierarchy";
import {
  deleteClassification,
  deleteClassificationValue,
  fetchClassifications,
  saveClassification,
  saveClassificationValue,
} from "./services/capitalFlowService";

function scopeSummary(scopes) {
  return (scopes || []).map((scope) => {
    const name = scope.object_type === "project" ? "Projects" : "Allocation components";
    const rules = [scope.is_required ? "required" : "optional", scope.allow_multiple ? "multiple" : "single"];
    return `${name}: ${rules.join(", ")}`;
  }).join(" · ") || "No assignment scopes configured";
}

export default function CapitalFlowClassifications() {
  const { selectedOrg, profile } = useProfile();
  const { alert, confirm } = useDialog();
  const [classifications, setClassifications] = useState([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [expandedValues, setExpandedValues] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingClassification, setEditingClassification] = useState(undefined);
  const [editingValue, setEditingValue] = useState(null);

  const load = useCallback(async () => {
    if (!selectedOrg?.id) {
      setClassifications([]);
      return;
    }
    setLoading(true);
    try {
      setClassifications(await fetchClassifications(selectedOrg.id, { includeArchived }));
    } catch (error) {
      await alert(error.message, { title: "Unable to Load Classifications" });
    } finally {
      setLoading(false);
    }
  }, [alert, includeArchived, selectedOrg?.id]);

  useEffect(() => { load(); }, [load]);

  const tenantId = selectedOrg?.tenant_id || profile?.tenant_id;
  const handleSaveClassification = async (classification, scopes) => {
    setSaving(true);
    try {
      await saveClassification({ classification, scopes, orgId: selectedOrg.id, tenantId, userId: profile.id });
      setEditingClassification(undefined);
      await load();
    } catch (error) {
      await alert(error.message, { title: "Unable to Save Classification" });
    } finally { setSaving(false); }
  };

  const handleSaveValue = async (value) => {
    setSaving(true);
    try {
      await saveClassificationValue({ value, classificationId: editingValue.classification.id, orgId: selectedOrg.id, tenantId, userId: profile.id });
      setEditingValue(null);
      await load();
    } catch (error) {
      await alert(error.message, { title: "Unable to Save Classification Value" });
    } finally { setSaving(false); }
  };

  const handleDeleteClassification = async () => {
    if (!editingClassification?.id) return;
    if (!await confirm(
      `Permanently delete Classification “${editingClassification.name}”? This cannot be undone.`,
      { title: "Delete Classification", okLabel: "Delete", cancelLabel: "Cancel" }
    )) return;
    setSaving(true);
    try {
      await deleteClassification({ classificationId: editingClassification.id, orgId: selectedOrg.id });
      setEditingClassification(undefined);
      await load();
    } catch (error) {
      await alert(error.message, { title: "Unable to Delete Classification" });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteValue = async () => {
    if (!editingValue?.value?.id) return;
    const { classification, value } = editingValue;
    if (!await confirm(`Permanently delete Classification Value “${getClassificationPath(classification.values, value.id)}”? This cannot be undone.`, { title: "Delete Classification Value", okLabel: "Delete", cancelLabel: "Cancel" })) return;
    setSaving(true);
    try {
      await deleteClassificationValue({ valueId: value.id, orgId: selectedOrg.id });
      setEditingValue(null);
      setExpandedValues((current) => {
        const next = new Set(current);
        next.delete(value.id);
        return next;
      });
      await load();
    } catch (error) { await alert(error.message, { title: "Unable to Delete Classification Value" }); }
    finally { setSaving(false); }
  };

  const toggle = (id) => setExpanded((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleValue = (id) => setExpandedValues((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <Page
      title="CapitalFlow - Classifications"
      subtitle="Configure organization-defined hierarchies and assignment rules."
      showOrganizationSelector
    >
      <SectionCard
        title="Classifications"
        subtitle={selectedOrg ? `${classifications.length} Classification${classifications.length === 1 ? "" : "s"}` : "Select an organization"}
        headerAction={<div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-muted"><input type="checkbox" checked={includeArchived} onChange={(event) => setIncludeArchived(event.target.checked)} /> Show archived</label>
          <button type="button" className="btn btn-ghost" onClick={load} disabled={!selectedOrg || loading} title="Refresh Classifications"><RefreshCw size={16} className={loading ? "animate-spin" : ""} /></button>
          <button type="button" className="btn btn-primary flex items-center gap-2" onClick={() => setEditingClassification(null)} disabled={!selectedOrg}><Plus size={16} /> New Classification</button>
        </div>}
      >
        {!selectedOrg ? <div className="py-8 text-center text-sm text-muted">Select an organization to manage Classifications.</div>
          : loading ? <div className="py-8 text-center text-sm text-muted">Loading Classifications…</div>
          : classifications.length ? <div className="space-y-3">{classifications.map((classification) => {
            const isExpanded = expanded.has(classification.id);
            const rows = flattenClassificationHierarchy(classification.values).filter((value) =>
              getClassificationAncestors(classification.values, value.id).every((ancestor) => expandedValues.has(ancestor.id))
            );
            return <div key={classification.id} className="rounded-lg border border-border">
              <div className="flex items-center gap-3 p-3">
                <div className="flex w-10 shrink-0 justify-center">
                  <button type="button" className="btn btn-ghost px-2 py-1" onClick={() => toggle(classification.id)} title={isExpanded ? "Collapse values" : "Expand values"}>{isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</button>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-sm leading-tight"><span className="font-semibold text-app">{classification.name}</span>{classification.code ? <span className="text-xs text-muted">({classification.code})</span> : null}{!classification.is_active ? <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted">Archived</span> : null}</div>
                  <div className="mt-0.5 text-xs leading-tight text-muted">{scopeSummary(classification.scopes)}</div>
                </div>
                <button type="button" className="btn btn-ghost px-2 py-1" onClick={() => setEditingValue({ classification, value: null })} disabled={!classification.is_active} title="Add Value"><Plus size={14} /> Value</button>
                <button type="button" className="btn btn-ghost px-2 py-1" onClick={() => setEditingClassification(classification)} title="Edit Classification"><Pencil size={14} /></button>
              </div>
              {isExpanded ? <div className="border-t border-border px-3 py-1">
                {rows.length ? rows.map((value) => {
                  const hasChildren = classification.values.some((candidate) => candidate.parent_value_id === value.id);
                  const isValueExpanded = expandedValues.has(value.id);
                  return <div key={value.id} className="flex items-center gap-3 border-b border-border/50 py-1 last:border-0" style={{ paddingLeft: `${value.depth * 1.5}rem` }}>
                  <div className="flex w-10 shrink-0 justify-center">
                    {hasChildren ? <button type="button" className="btn btn-ghost px-2 py-1" onClick={() => toggleValue(value.id)} title={isValueExpanded ? "Collapse child values" : "Expand child values"}>{isValueExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</button> : null}
                  </div>
                  <div className="min-w-0 flex-1 text-sm leading-tight"><span className={value.is_active ? "text-app" : "text-muted line-through"}>{value.name}</span>{value.code ? <span className="ml-2 text-xs text-muted">{value.code}</span> : null}<div className="text-[11px] leading-tight text-muted">{getClassificationPath(classification.values, value.id)}</div></div>
                  <button type="button" className="btn btn-ghost px-2 py-1" onClick={() => setEditingValue({ classification, value })} title="Edit or Move Value"><Pencil size={13} /></button>
                </div>;
                }) : <div className="py-4 text-center text-sm text-muted">No Classification Values.</div>}
              </div> : null}
            </div>;
          })}</div> : <div className="py-8 text-center text-sm text-muted">No Classifications have been configured.</div>}
      </SectionCard>

      {editingClassification !== undefined ? <ClassificationModal classification={editingClassification} saving={saving} onSave={handleSaveClassification} onDelete={handleDeleteClassification} onClose={() => setEditingClassification(undefined)} /> : null}
      {editingValue ? <ClassificationValueModal classification={editingValue.classification} value={editingValue.value} saving={saving} onSave={handleSaveValue} onDelete={handleDeleteValue} onClose={() => setEditingValue(null)} /> : null}
    </Page>
  );
}
